import { Aide } from '@/components/ui/aide';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  operationsAPI,
  trucksAPI,
  driversAPI,
  printingAPI,
  corridorsAPI,
} from '../lib/api';
import {
  Plus,
  Loader2,
  Search,
  Pencil,
  Trash2,
  X,
  Filter,
  MoreHorizontal,
  Eye,
  Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePaginationServeur } from '../hooks/usePagination';
import { useDerniereDemande } from '../hooks/useDerniereDemande';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from '../components/ui/sonner';
import { FieldError } from '../components/ui/field-error';
import { cn, formatDate } from '../lib/utils';
import { formatDateLongue } from '../lib/formats';
import { messageErreur } from '../lib/erreurs';
import { referenceLisible, texteSur } from '../lib/libelles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { SectionCard } from '../components/dashboard';
import { PageHero } from '../components/dashboard/PageHero';
import { LoadingState } from '../components/ui/loading-state';
import { useDebouncedValue } from '../hooks/use-debounced-value';
import { useVerificationFlotte } from '../hooks/useVerificationFlotte';
import { reperePropriete, truckOption } from '../lib/selectOptions';
import { filtrerOptions } from '../components/ui/searchable-select';
import { EmptyState } from '../components/ui/empty-state';
import { Convoi } from '../components/ui/icons/domain-icons';
import { PrintMenu } from '../components/business/PrintMenu';
import { ChampsAvances } from '../components/ui/champs-avances';
import { fetchAllPages } from '../lib/fetchAllPages';
import { driverOption } from '../lib/selectOptions';
import { useRafraichirSur } from '../hooks/useRafraichirSur';

const todayIso = () => new Date().toISOString().split('T')[0];

// Sans corridor installé, le trajet se saisit en texte libre et part de ce
// libellé par défaut.
const DEFAULT_ROUTE = 'Mali - Sénégal - Mali';

/** Le formulaire d'un convoi neuf: une seule définition pour tous les écrans. */
export const emptyConvoyForm = (corridors = []) => ({
  code: '',
  departure_date: todayIso(),
  // Sans corridor connu on retombe sur l'ancien texte par défaut; avec, le
  // convoi doit désigner son corridor et rien n'est pré-rempli.
  route: corridors.length > 0 ? '' : DEFAULT_ROUTE,
  corridor_id: '',
  truck_ids: [],
  description: '',
  active: true,
  planned_return_date: '',
  convoy_chief_driver_id: '',
});

/**
 * Le formulaire reconstruit depuis un convoi existant.
 *
 * `duplicate` repart d'un code et d'un départ neufs, sans camions (ceux du
 * convoi d'origine sont déjà engagés) ni retour prévu (la date appartenait au
 * départ d'origine). Le chef de convoi, lui, est conservé: c'est le plus
 * souvent la même équipe qui repart.
 */
export const convoyToForm = (operation, corridors = [], { duplicate = false } = {}) => {
  const base = emptyConvoyForm(corridors);
  // Un convoi créé avant l'arrivée du référentiel porte un texte libre qui ne
  // correspond à aucun corridor: on ne le devine pas, et on ne l'écrase pas
  // non plus — la liste s'affiche vide et le libellé reste visible.
  const corridorId = (corridors.find((c) => c.name === operation.route) || {}).id || '';
  return {
    ...base,
    code: duplicate ? '' : operation.code || '',
    departure_date: duplicate
      ? base.departure_date
      : operation.departure_date || base.departure_date,
    route: duplicate ? operation.route || base.route : operation.route || '',
    corridor_id: corridorId,
    truck_ids: duplicate ? [] : operation.truck_ids || [],
    description: operation.description || '',
    active: duplicate ? true : operation.active !== false,
    planned_return_date: duplicate ? '' : operation.planned_return_date || '',
    convoy_chief_driver_id: operation.convoy_chief_driver_id || '',
  };
};

/** Message d'erreur du retour prévu, ou null s'il est cohérent avec le départ. */
export const returnDateError = (form) =>
  form.planned_return_date &&
  form.departure_date &&
  form.planned_return_date < form.departure_date
    ? 'Le retour prévu ne peut pas précéder la date de départ'
    : null;

/**
 * Retour prévu et chef de convoi, repliés: deux informations qu'on ne connaît
 * pas toujours au départ, et que le convoi sait afficher quand elles sont là.
 */
export const ConvoyOptionalFields = ({ form, setForm, driverOptions, idPrefix }) => {
  const error = returnDateError(form);
  const chiefSelected = Boolean(form.convoy_chief_driver_id);
  return (
    <ChampsAvances
      titre="Retour et chef de convoi"
      consequence="Date de retour prévue et chauffeur responsable du convoi."
      nombre={2}
      valeurs={[form.planned_return_date, form.convoy_chief_driver_id]}
    >
      <div>
        <Label className="label-base">Retour prévu</Label>
        <Input
          type="date"
          value={form.planned_return_date || ''}
          min={form.departure_date || undefined}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, planned_return_date: e.target.value }))
          }
          className="input-base"
          data-testid={`${idPrefix}-planned-return-date`}
        />
        <FieldError show={Boolean(error)}>{error}</FieldError>
      </div>
      <div>
        <Label className="label-base">Chef de convoi</Label>
        <SearchableSelect
          className="input-base"
          data-testid={`${idPrefix}-convoy-chief`}
          placeholder="Choisir un chauffeur"
          searchPlaceholder="Rechercher un chauffeur…"
          emptyMessage="Aucun chauffeur trouvé"
          value={form.convoy_chief_driver_id || ''}
          onValueChange={(driverId) =>
            setForm((prev) => ({ ...prev, convoy_chief_driver_id: driverId }))
          }
          options={driverOptions}
        />
        {chiefSelected ? (
          <button
            type="button"
            className="mt-1 text-xs text-muted-foreground underline-offset-4 hover:underline"
            onClick={() =>
              setForm((prev) => ({ ...prev, convoy_chief_driver_id: '' }))
            }
            data-testid={`${idPrefix}-convoy-chief-clear`}
          >
            Retirer le chef de convoi
          </button>
        ) : null}
      </div>
    </ChampsAvances>
  );
};

// Un convoi est « en cours » tant qu'il est actif ET que le serveur ne l'a pas
// clôturé (`closed_at`, posé quand tous ses camions ont passé la douane
// d'entrée). Lire seulement `active` laissait « En cours » un convoi dont les
// trajets étaient tous terminés: `active` est une décision d'exploitant (le
// passer à faux archive les trajets), la clôture un constat.
export const convoiEnCours = (operation) =>
  operation?.active !== false && !operation?.closed_at;

// Filtre d'état de l'écran -> `status_group` de l'API: le serveur filtre avant
// de découper en pages, sinon un filtre ne trierait que la page affichée.
const STATUS_GROUP_SERVEUR = {
  actifs: 'active',
  inactifs: 'inactive',
  'sans-camion': 'no_truck',
};

const OperationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [operations, setOperations] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Le squelette ne remplace la page qu'au tout premier chargement:
  // un rafraîchissement démontait le champ de recherche en cours de frappe.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // La frappe reste immédiate à l'écran; seule la requête attend.
  const debouncedSearch = useDebouncedValue(searchQuery);
  // Les filtres restent repliés par défaut: neuf convois sur dix se trouvent
  // à la recherche, et une rangée de puces en permanence volerait la place de
  // la première ligne de la liste.
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('tous');
  const [totalOperations, setTotalOperations] = useState(0);
  // Les quatre chiffres de l'en-tête, comptés par le serveur sur toute la
  // recherche: la page reçue n'en montre qu'une tranche.
  const [serverCounts, setServerCounts] = useState(null);
  // Pagination commune: page et taille dans l'adresse, barre sous la liste.
  // Une recherche ou un filtre ramène à la première page.
  const pagination = usePaginationServeur(totalOperations, {
    reinitialiser: `${debouncedSearch}|${statusFilter}`,
  });
  const { page, limit: pageSize } = pagination;
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [operationToEdit, setOperationToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newSubmitAttempted, setNewSubmitAttempted] = useState(false);
  const [editSubmitAttempted, setEditSubmitAttempted] = useState(false);
  const [truckQuery, setTruckQuery] = useState('');
  const [formData, setFormData] = useState(() => ({
    ...emptyConvoyForm(),
    route: '',
  }));
  const [corridors, setCorridors] = useState([]);
  const [drivers, setDrivers] = useState([]);

  // Le référentiel des corridors ne dépend ni de la page ni de la recherche:
  // on le lit une fois. Il peut être vide — un déploiement où
  // `seed_corridors` n'a pas encore tourné — et l'écran doit rester utilisable
  // dans ce cas, d'où le repli sur la saisie libre plus bas.
  useEffect(() => {
    let cancelled = false;
    corridorsAPI
      .getAll()
      .then((rows) => {
        if (!cancelled) setCorridors(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setCorridors([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Le chef de convoi se choisit parmi tous les chauffeurs: une liste coupée
  // à 200 rendait introuvable le deux-cent-unième.
  useEffect(() => {
    let cancelled = false;
    fetchAllPages((params) => driversAPI.getAllPaged(params))
      .then((result) => {
        if (!cancelled) setDrivers(result.items || []);
      })
      .catch(() => {
        if (!cancelled) setDrivers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const driverOptions = useMemo(() => drivers.map(driverOption), [drivers]);

  const nouvelleDemande = useDerniereDemande();
  const loadData = useCallback(async ({ silencieux = false } = {}) => {
    const perimee = nouvelleDemande();
    try {
      if (!silencieux) setIsLoading(true);
      const opsPage = await operationsAPI.getAllPaged({
        page,
        page_size: pageSize,
        q: debouncedSearch,
        ...(STATUS_GROUP_SERVEUR[statusFilter]
          ? { status_group: STATUS_GROUP_SERVEUR[statusFilter] }
          : {}),
      });
      if (perimee()) return;
      const opItems = opsPage.items || [];
      setOperations(opItems);
      setTotalOperations(opsPage.total ?? opItems.length);
      setServerCounts(opsPage.counts || null);
    } catch (err) {
      if (perimee()) return;
      console.error('Failed to load operations:', err);
      toast.error(messageErreur(err, 'Impossible de charger les convois'));
    } finally {
      if (!perimee()) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  }, [page, pageSize, debouncedSearch, statusFilter, nouvelleDemande]);

  // Tout le parc, pas une page: le choix des camions du convoi et la plaque
  // affichée sur chaque ligne doivent retrouver le millième camion. Chargé à
  // part des convois pour ne pas être refait à chaque frappe de recherche.
  const loadTrucks = useCallback(async () => {
    try {
      const result = await fetchAllPages((params) => trucksAPI.getAllPaged(params));
      setTrucks(result.items || []);
    } catch (err) {
      console.error('Failed to load trucks:', err);
      toast.error(messageErreur(err, 'Impossible de charger les camions'));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, location]);

  useEffect(() => {
    loadTrucks();
  }, [loadTrucks, location]);

  // Une écriture faite ailleurs (autre écran, autre onglet, file hors ligne rejouée) recharge ces chiffres: jamais de compteur figé.
  useRafraichirSur(['convois', 'trajets', 'repartition', 'camions', 'chauffeurs', 'depenses'], (options) => Promise.all([loadData(options), loadTrucks(options)]));


  const useCorridorPicker = corridors.length > 0;
  const corridorOptions = useMemo(
    () =>
      corridors.map((corridor) => ({
        value: corridor.id,
        label: texteSur(corridor.name, 'Corridor sans nom'),
        keywords: [
          corridor.code,
          corridor.origin_label,
          corridor.destination_label,
          ...(corridor.aliases || []),
        ],
      })),
    [corridors]
  );

  const selectCorridor = (corridorId) => {
    const corridor = corridors.find((c) => c.id === corridorId);
    setFormData((prev) => ({
      ...prev,
      corridor_id: corridorId,
      // Le texte reste stocké: il alimente l'impression du bon de route, les
      // exports et la recherche, qui ne connaissent pas les corridors.
      route: corridor ? corridor.name : prev.route,
    }));
  };

  const selectableTrucks = trucks.filter((t) => t?.current_status === 'FREE');
  const selectedTrucks = formData.truck_ids
    .map((id) => trucks.find((t) => t.id === id))
    .filter(Boolean);

  // Par défaut: les premiers camions libres non choisis. La saisie élargit à
  // tout le parc libre, sur la plaque, la citerne, les châssis, le chauffeur
  // et le transporteur (mêmes mots-clés que les menus de choix).
  const TRUCKS_PAR_DEFAUT = 12;
  const TRUCKS_PAR_RECHERCHE = 50;
  const { truckSuggestions, truckMatchesBusy } = (() => {
    const q = truckQuery.trim();
    const selected = new Set(formData.truck_ids);
    const base = selectableTrucks.filter((t) => !selected.has(t.id));
    if (!q) return { truckSuggestions: base.slice(0, TRUCKS_PAR_DEFAUT), truckMatchesBusy: 0 };
    const byId = new Map(trucks.map((t) => [t.id, t]));
    const trouves = filtrerOptions(base.map(truckOption), q)
      .map((option) => byId.get(option.value))
      .filter(Boolean);
    const busy = trucks.filter((t) => t?.current_status !== 'FREE' && !selected.has(t.id));
    return {
      truckSuggestions: trouves.slice(0, TRUCKS_PAR_RECHERCHE),
      truckMatchesBusy: filtrerOptions(busy.map(truckOption), q).length,
    };
  })();

  // Un camion loué dont le contrat est terminé à la date de départ ne peut plus
  // partir: on le signale dans la liste et sur les camions déjà choisis (la
  // date de départ peut changer après le choix). Le serveur garde le dernier mot.
  const fleetCheck = useVerificationFlotte({
    camionsIds: [...truckSuggestions, ...selectedTrucks]
      .filter((t) => t.ownership_type !== 'PROPRE')
      .map((t) => t.id),
    date: formData.departure_date,
  });
  const returnedSelectedTrucks = selectedTrucks.filter((t) => fleetCheck.estRendu(t.id));

  const addTruck = (truckId) => {
    if (!truckId) return;
    if (fleetCheck.estRendu(truckId)) return;
    setFormData((prev) => ({
      ...prev,
      truck_ids: prev.truck_ids.includes(truckId)
        ? prev.truck_ids
        : [...prev.truck_ids, truckId],
    }));
    setTruckQuery('');
  };

  const removeTruck = (truckId) => {
    setFormData((prev) => ({
      ...prev,
      truck_ids: prev.truck_ids.filter((id) => id !== truckId),
    }));
  };

  // Plus d'aller-retour serveur pour obtenir un code: il est fabriqué à la
  // création, par une séquence atomique. Ouvrir le formulaire ne dépend donc
  // plus du réseau — l'ancien appel pouvait refuser l'ouverture tout entière.
  const openNewModal = () => {
    setNewSubmitAttempted(false);
    setFormData(emptyConvoyForm(corridors));
    setShowNewModal(true);
  };

  // « Dupliquer » ne recrée rien tout seul, et c'est volontaire: un convoi
  // exige des camions libres, or ceux du convoi d'origine sont déjà engagés.
  // On rouvre donc le formulaire avec un code neuf et le même trajet, et
  // l'exploitant désigne les camions du nouveau départ.
  const duplicateOperation = (operation) => {
    setNewSubmitAttempted(false);
    setFormData(convoyToForm(operation, corridors, { duplicate: true }));
    setTruckQuery('');
    setShowNewModal(true);
  };

  const handleSubmit = async () => {
    setNewSubmitAttempted(true);
    const corridorMissing = useCorridorPicker && !formData.corridor_id;
    if (!formData.departure_date || !formData.route || corridorMissing) {
      toast.error('Veuillez remplir les champs requis');
      return;
    }
    if (!formData.truck_ids || formData.truck_ids.length === 0) {
      toast.error('Veuillez sélectionner au moins un camion');
      return;
    }
    if (returnDateError(formData)) {
      toast.error(returnDateError(formData));
      return;
    }
    if (returnedSelectedTrucks.length > 0) {
      toast.error(
        `Retirez du convoi : ${returnedSelectedTrucks.map((t) => t.matricule).join(', ')} (rendu au propriétaire, ne peut plus partir).`
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const departureDate = formData.departure_date;

      // Les pays ne partent plus d'ici. Le corridor les porte, et c'est le
      // serveur qui les recopie sur chaque trajet créé. Cet écran les déduisait
      // par découpage du libellé de trajet — ce qui donnait « Mali → Mali » sur
      // un aller-retour — et retombait sinon sur « Sénégal → Mali » écrit en
      // dur: deux façons de deviner une information que la base détenait.
      const scheduledTrips = {
        truck_ids: formData.truck_ids,
        ...(formData.corridor_id ? { corridor_id: formData.corridor_id } : {}),
      };

      // `corridor_id` appartient aux trajets, pas au convoi: il part dans
      // `scheduled_trips` et non à la racine.
      // Retour prévu et chef de convoi sont facultatifs: absents plutôt que
      // vides, le serveur les traite comme « non précisé ».
      const {
        corridor_id: _corridorId,
        planned_return_date: plannedReturnDate,
        convoy_chief_driver_id: chiefDriverId,
        ...operationFields
      } = formData;

      // Send trip data with operation creation
      await operationsAPI.create({
        ...operationFields,
        ...(plannedReturnDate ? { planned_return_date: plannedReturnDate } : {}),
        ...(chiefDriverId ? { convoy_chief_driver_id: chiefDriverId } : {}),
        scheduled_trips: scheduledTrips,
      });

      setShowNewModal(false);
      const today = new Date().toISOString().split('T')[0];
      if (departureDate === today) {
        toast.success(
          `Convoi créé : ${formData.truck_ids.length} trajet${formData.truck_ids.length > 1 ? 's' : ''} lancé${formData.truck_ids.length > 1 ? 's' : ''} aujourd’hui`,
        );
      } else {
        toast.success(`Convoi créé pour le ${formatDateLongue(departureDate)}`);
      }
      setFormData(emptyConvoyForm(corridors));
      setTruckQuery('');
      setNewSubmitAttempted(false);
      loadData();
      loadTrucks();
    } catch (err) {
      console.error('Failed to create operation:', err);
      toast.error(messageErreur(err, 'Le convoi n’a pas pu être créé'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEdit = (operation) => {
    setOperationToEdit(operation);
    setEditSubmitAttempted(false);
    setFormData(convoyToForm(operation, corridors));
    setTruckQuery('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!operationToEdit) return;
    setEditSubmitAttempted(true);
    if (!formData.departure_date || !formData.route) {
      toast.error('Veuillez remplir les champs requis');
      return;
    }
    if (returnDateError(formData)) {
      toast.error(returnDateError(formData));
      return;
    }

    setIsSubmitting(true);
    try {
      await operationsAPI.update(operationToEdit.id, {
        // Absent à la création: le serveur le fabrique par sa séquence, qui
        // est atomique et comptée par société. L'écran le calculait en
        // comptant les convois du jour — deux sociétés créant un convoi le
        // même jour se disputaient le même code.
        ...(formData.code ? { code: formData.code } : {}),
        departure_date: formData.departure_date,
        route: formData.route,
        description: formData.description,
        active: formData.active,
        // `null` efface: la liste de champs modifiés doit pouvoir retirer un
        // retour prévu ou un chef de convoi saisis par erreur.
        planned_return_date: formData.planned_return_date || null,
        convoy_chief_driver_id: formData.convoy_chief_driver_id || null,
      });
      setShowEditModal(false);
      setOperationToEdit(null);
      toast.success('Convoi mis à jour');
      setEditSubmitAttempted(false);
      await loadData();
      loadTrucks();
    } catch (err) {
      console.error('Failed to update operation:', err);
      toast.error(messageErreur(err, 'Le convoi n’a pas pu être modifié'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = (operation) => {
    setOperationToDelete(operation);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!operationToDelete) return;
    setIsSubmitting(true);
    try {
      await operationsAPI.delete(operationToDelete.id);
      setShowDeleteModal(false);
      setOperationToDelete(null);
      toast.success('Convoi supprimé');
      await loadData();
      loadTrucks();
    } catch (err) {
      console.error('Failed to delete operation:', err);
      toast.error(messageErreur(err, 'Le convoi n’a pas pu être supprimé'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Le serveur applique la recherche et le filtre d'état avant de découper en
  // pages (`status_group`). Le même filtre s'applique encore ici à la page
  // reçue: il ne change rien en ligne, et garde le cache hors ligne cohérent,
  // qui renvoie tous les convois.
  const FILTRES_ETAT = [
    { id: 'tous', label: 'Tous' },
    { id: 'actifs', label: 'En cours' },
    { id: 'inactifs', label: 'Terminés ou suspendus' },
    { id: 'sans-camion', label: 'Sans camion' },
  ];

  const matchFiltre = (operation) => {
    if (statusFilter === 'actifs') return convoiEnCours(operation);
    if (statusFilter === 'inactifs') return !convoiEnCours(operation);
    if (statusFilter === 'sans-camion')
      return !Array.isArray(operation.truck_ids) || operation.truck_ids.length === 0;
    return true;
  };

  const filteredOperations = operations.filter(matchFiltre);
  // Recherche et filtre disent la même chose à l'état vide: « vos convois sont
  // toujours là ». C'est la distinction « rien » / « rien qui corresponde ».
  const isFiltered = Boolean(searchQuery) || statusFilter !== 'tous';

  /** Le libellé du camion rattaché: la plaque quand il est seul, le compte sinon. */
  const truckLabel = (operation) => {
    const ids = Array.isArray(operation.truck_ids) ? operation.truck_ids : [];
    if (ids.length === 0) return null;
    if (ids.length === 1) {
      const truck = trucks.find((t) => t.id === ids[0]);
      return truck?.matricule || '1 camion';
    }
    return `${ids.length} camions`;
  };

  /**
   * L'échéance en clair sous la date. Une date seule oblige à compter dans sa
   * tête pour savoir si le départ est demain ou dans un trimestre.
   */
  const departureHint = (dateString) => {
    if (!dateString) return null;
    const cible = new Date(dateString);
    if (Number.isNaN(cible.getTime())) return null;
    const JOUR = 24 * 60 * 60 * 1000;
    const depuis = new Date().setHours(0, 0, 0, 0);
    const jusqua = cible.setHours(0, 0, 0, 0);
    const jours = Math.round((jusqua - depuis) / JOUR);
    if (jours === 0) return "Aujourd'hui";
    const n = Math.abs(jours);
    const duree =
      n < 31
        ? `${n} jour${n > 1 ? 's' : ''}`
        : n < 365
          ? `${Math.round(n / 30)} mois`
          : `${Math.round(n / 365)} an${Math.round(n / 365) > 1 ? 's' : ''}`;
    return jours > 0 ? `Dans ${duree}` : `Il y a ${duree}`;
  };

  // Chiffres du serveur quand il les donne (toute la recherche); sinon ceux de
  // la liste reçue, qui est alors la liste entière (hors ligne).
  const localEnCours = operations.filter(convoiEnCours).length;
  const localAvecCamions = operations.filter((operation) => Array.isArray(operation.truck_ids) && operation.truck_ids.length > 0).length;
  const activeOperations = serverCounts?.enCours ?? localEnCours;
  const inactiveOperations = serverCounts?.termines ?? Math.max(0, operations.length - localEnCours);
  const withoutTrucksOperations = serverCounts?.sansCamion ?? Math.max(0, operations.length - localAvecCamions);
  const withTrucksOperations = serverCounts
    ? Math.max(0, serverCounts.enCours + serverCounts.termines - serverCounts.sansCamion)
    : localAvecCamions;

  if (isLoading && !hasLoaded) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="cards" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="operations-page">
      <PageHero
        accroche="Plus loin ensemble"
        illustration="transport"
        statsDisposition="ligne"
        eyebrow="Exploitation"
        title="Convois"
        description="Suivez vos convois, leurs camions et leur avancement."
        stats={[
          {
            label: 'En cours',
            valeur: activeOperations,
            aide: 'Convois en cours',
          },
          {
            label: 'Avec camions',
            valeur: withTrucksOperations,
            aide: 'Convois qui ont au moins un camion',
          },
          {
            label: 'Sans camion',
            valeur: withoutTrucksOperations,
            ton: withoutTrucksOperations ? 'critique' : 'neutre',
            aide: 'Convois sans camion: ils ne peuvent pas partir',
            onClick: withoutTrucksOperations
              ? () => {
                  setStatusFilter('sans-camion');
                  setShowFilters(true);
                }
              : undefined,
          },
          {
            label: 'Terminés ou suspendus',
            valeur: inactiveOperations,
          },
        ]}
        actions={
          <Button onClick={openNewModal} data-testid="new-operation-btn">
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouveau convoi
          </Button>
        }
      />

      {/* Recherche et filtres sur une seule bande: la recherche
          est bornée en grand écran pour laisser la place à droite, et la
          liste commence plus haut. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 lg:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
          <Input
            placeholder="Rechercher un convoi par code, trajet ou date…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-base pl-10"
            data-testid="operations-search"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((v) => !v)}
        >
          <Filter className="h-4 w-4" aria-hidden="true" />
          Filtres
          {statusFilter !== 'tous' ? (
            // Un filtre actif replié est invisible: la pastille évite qu'on
            // croie la liste vide alors qu'elle est seulement restreinte.
            <span className="ml-1 rounded-full bg-primary/15 px-1.5 text-[11px] font-bold text-primary">
              1
            </span>
          ) : null}
        </Button>

        {showFilters ? (
          <div className="flex flex-wrap gap-2">
            {FILTRES_ETAT.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setStatusFilter(id)}
                aria-pressed={statusFilter === id}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  statusFilter === id
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {operations.length === 0 && !isFiltered ? (
        <EmptyState
          illustration="donnee"
          title="Aucun convoi enregistré"
          description="Un convoi regroupe plusieurs camions qui partent ensemble sur le même itinéraire, à la même date. Il crée d’un coup les trajets de tous les camions du groupe."
          // La création exige au moins un camion (validation du formulaire):
          // sans parc, ouvrir le formulaire ne mène qu'à un refus, donc le
          // bouton emmène d'abord là où le camion se crée.
          actionLabel={
            trucks.length === 0 ? 'Ajouter un camion d’abord' : 'Créer un convoi'
          }
          onAction={
            trucks.length === 0 ? () => navigate('/trucks') : openNewModal
          }
        />
      ) : (
        <SectionCard
          icon={Convoi}
          title={`${totalOperations || operations.length} convoi${(totalOperations || operations.length) > 1 ? 's' : ''} au total`}
          contentClassName="space-y-3"
        >
          {filteredOperations.length === 0 ? (
            <EmptyState
              variant="filtered"
              icon={Convoi}
              title="Aucun résultat"
              description="Aucun convoi ne correspond à cette recherche ou à ce filtre. Vos convois sont toujours là : videz la recherche pour les revoir."
              className="border-0"
              actionLabel={isFiltered ? 'Réinitialiser' : undefined}
              onAction={() => {
                setSearchQuery('');
                setStatusFilter('tous');
              }}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Convoi</TableHead>
                  <TableHead>Trajet</TableHead>
                  <TableHead>Départ</TableHead>
                  <TableHead>Camions</TableHead>
                  <TableHead>État</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOperations.map((operation) => {
                  const actif = convoiEnCours(operation);
                  const camions = truckLabel(operation);
                  const echeance = departureHint(operation.departure_date);

                  return (
                    <TableRow
                      key={operation.id}
                      className={cn('cursor-pointer', !actif && 'opacity-70')}
                      onClick={() => navigate(`/operations/${operation.id}`)}
                      data-testid={`operation-card-${operation.id}`}
                    >
                      <TableCell className="whitespace-nowrap font-mono font-semibold">
                        {referenceLisible(operation.code, 'Convoi sans code')}
                      </TableCell>
                      <TableCell className="max-w-[18rem] truncate">
                        {operation.route ? texteSur(operation.route, 'À définir') : 'À définir'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(operation.departure_date)}
                        {echeance ? (
                          <span className="block text-xs text-muted-foreground">
                            {echeance}
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {camions || (
                          <span className="font-medium text-destructive">
                            Aucun camion
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            'status-badge gap-1.5',
                            actif
                              ? 'border-success/40 bg-success/15 text-success'
                              : 'border-border bg-muted text-muted-foreground'
                          )}
                        >
                          {actif ? 'En cours' : 'Terminé ou suspendu'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {/* La ligne entière navigue au clic: ouvrir un menu ne
                            doit pas emmener l'utilisateur sur le détail. */}
                        <div
                          role="presentation"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center justify-end gap-1"
                        >
                          <PrintMenu
                            label="Feuille"
                            variant="ghost"
                            items={[
                              {
                                label: `Feuille de convoi ${operation.code || ''}`.trim(),
                                filename: `convoi-${operation.code || 'sans-code'}`,
                                url: printingAPI.convoy(operation.id),
                                formats: ['pdf', 'xlsx'],
                              },
                            ]}
                          />
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label={`Actions du convoi ${operation.code || ''}`.trim()}
                              >
                                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem
                                onSelect={() =>
                                  navigate(`/operations/${operation.id}`)
                                }
                              >
                                <Eye className="h-4 w-4" aria-hidden="true" />
                                Voir les détails
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => openEdit(operation)}
                                data-testid={`edit-operation-${operation.id}`}
                              >
                                <Pencil className="h-4 w-4" aria-hidden="true" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => duplicateOperation(operation)}
                              >
                                <Copy className="h-4 w-4" aria-hidden="true" />
                                Dupliquer
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => confirmDelete(operation)}
                                data-testid={`delete-operation-${operation.id}`}
                              >
                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </SectionCard>
      )}

      {filteredOperations.length > 0 ? <BarrePagination pagination={pagination} nom="convois" /> : null}

      {/* Le panneau « Tout est sous contrôle », dessin compris, fermait l'écran
          sur une félicitation et sur un total déjà écrit deux fois au-dessus —
          dans la ligne de chiffres et dans le titre de la liste. Il ne restait
          rien à en tirer, et il coûtait un écran de défilement à chaque
          ouverture. */}

      {/* New Operation Modal */}
      <Dialog
        open={showNewModal}
        onOpenChange={(open) => {
          setShowNewModal(open);
          if (!open) setNewSubmitAttempted(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau convoi</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="label-base">Date de départ *</Label>
              <Input
                type="date"
                value={formData.departure_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    departure_date: e.target.value,
                  }))
                }
                className="input-base"
                data-testid="new-operation-departure-date"
              />
              <FieldError show={newSubmitAttempted && !formData.departure_date}>
                Date requise
              </FieldError>
            </div>

            <div>
              <div className="flex items-center gap-1">
                <Label className="label-base">Trajet (corridor) *</Label>
                <Aide titre="Trajet (corridor)">
                  {useCorridorPicker
                    ? 'Le corridor porte le tracé, la distance restante et les postes frontières qui font avancer le trajet.'
                    : "Aucun corridor n'est encore installé: le 1er et le dernier pays servent pour les trajets."}
                </Aide>
              </div>
              {useCorridorPicker ? (
                <>
                  <SearchableSelect
                    className="input-base"
                    data-testid="new-operation-route"
                    placeholder="Choisir un corridor"
                    searchPlaceholder="Rechercher un corridor…"
                    emptyMessage="Aucun corridor trouvé"
                    value={formData.corridor_id || ''}
                    onValueChange={selectCorridor}
                    options={corridorOptions}
                  />
                  <FieldError show={newSubmitAttempted && !formData.corridor_id}>
                    Corridor requis
                  </FieldError>
                </>
              ) : (
                <>
                  <Input
                    placeholder="Ex: Mali - Sénégal - Mali"
                    value={formData.route}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, route: e.target.value }))
                    }
                    className="input-base"
                    data-testid="new-operation-route"
                  />
                  <FieldError show={newSubmitAttempted && !formData.route}>
                    Champ requis
                  </FieldError>
                </>
              )}
            </div>

            <div>
              <Label className="label-base">Camions du convoi *</Label>
              <Input
                placeholder="Rechercher par plaque, citerne, châssis ou chauffeur..."
                value={truckQuery}
                onChange={(e) => setTruckQuery(e.target.value)}
                className="input-base"
                data-testid="new-operation-truck-search"
              />

              <p
                className="text-xs text-muted-foreground mt-1"
                data-testid="convoy-truck-count"
              >
                {selectedTrucks.length === 0
                  ? 'Aucun camion sélectionné'
                  : `${selectedTrucks.length} sélectionné${selectedTrucks.length > 1 ? 's' : ''}`}
              </p>

              {selectedTrucks.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedTrucks.map((t) => (
                    <Badge
                      key={t.id}
                      className={cn(
                        'status-badge',
                        fleetCheck.estRendu(t.id) && 'border-destructive/40 bg-destructive/10 text-destructive'
                      )}
                      data-testid={fleetCheck.estRendu(t.id) ? 'convoy-truck-returned' : undefined}
                    >
                      <span className="font-mono font-bold">{t.matricule}</span>
                      <button
                        type="button"
                        className="ml-2 inline-flex"
                        onClick={() => removeTruck(t.id)}
                        aria-label="Retirer"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : null}

              <FieldError
                show={
                  newSubmitAttempted &&
                  (!formData.truck_ids || formData.truck_ids.length === 0)
                }
              >
                Sélectionnez au moins un camion
              </FieldError>

              {returnedSelectedTrucks.length > 0 ? (
                <div
                  role="alert"
                  className="mt-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
                  data-testid="convoy-returned-warning"
                >
                  {returnedSelectedTrucks.map((t) => (
                    <p key={t.id}>
                      <span className="font-mono font-bold">{t.matricule}</span> :{' '}
                      {fleetCheck.raison(t.id)}
                      <span className="block">
                        Retirez-le du convoi pour pouvoir enregistrer.
                      </span>
                    </p>
                  ))}
                </div>
              ) : null}

              <div className="mt-2 border border-border/60 rounded-md overflow-hidden">
                <div className="max-h-40 overflow-auto">
                  {truckSuggestions.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">
                      Aucun camion disponible
                    </div>
                  ) : (
                    truckSuggestions.map((t) => {
                      const rendu = fleetCheck.estRendu(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          disabled={rendu}
                          aria-disabled={rendu}
                          onClick={() => addTruck(t.id)}
                          className={cn(
                            'w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3',
                            rendu ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted/40'
                          )}
                          data-testid={rendu ? 'convoy-truck-option-returned' : undefined}
                        >
                          <span className="font-mono font-bold">
                            {t.matricule}
                          </span>
                          <span
                            className={cn(
                              'text-xs text-right',
                              rendu ? 'text-destructive' : 'text-muted-foreground'
                            )}
                          >
                            {rendu
                              ? fleetCheck.raison(t.id)
                              : ['Libre', reperePropriete(t)].filter(Boolean).join(' · ')}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1" data-testid="convoy-truck-hint">
                {truckQuery.trim()
                  ? `${truckSuggestions.length} camion${truckSuggestions.length > 1 ? 's' : ''} libre${truckSuggestions.length > 1 ? 's' : ''} trouvé${truckSuggestions.length > 1 ? 's' : ''}${
                      truckSuggestions.length >= TRUCKS_PAR_RECHERCHE ? ' (précisez la recherche pour en voir d’autres)' : ''
                    }.${
                      truckMatchesBusy > 0
                        ? ` ${truckMatchesBusy} autre${truckMatchesBusy > 1 ? 's' : ''} correspondent mais ne sont pas libres.`
                        : ''
                    }`
                  : selectableTrucks.length > truckSuggestions.length + selectedTrucks.length
                    ? `Seuls les camions libres apparaissent ici. ${truckSuggestions.length} affichés sur ${selectableTrucks.length - selectedTrucks.length}: tapez pour chercher parmi les autres.`
                    : 'Seuls les camions libres apparaissent ici.'}
              </p>
            </div>

            <div>
              <Label className="label-base">Description</Label>
              <Textarea
                placeholder="Description du convoi..."
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="input-base min-h-[80px]"
                data-testid="new-operation-description"
              />
            </div>

            <ConvoyOptionalFields
              form={formData}
              setForm={setFormData}
              driverOptions={driverOptions}
              idPrefix="new-operation"
            />

            <div className="flex items-center justify-between">
              <Label className="label-base mb-0">Convoi en cours</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, active: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !formData.departure_date ||
                !formData.route ||
                !formData.truck_ids ||
                formData.truck_ids.length === 0 ||
                Boolean(returnDateError(formData))
              }
              data-testid="submit-new-operation"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Créer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Operation Modal */}
      <Dialog
        open={showEditModal}
        onOpenChange={(open) => {
          setShowEditModal(open);
          if (!open) setEditSubmitAttempted(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier convoi</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="label-base">Code convoi</Label>
              <Input
                value={formData.code}
                disabled
                className="input-base font-mono bg-muted/50"
              />
            </div>

            <div>
              <Label className="label-base">Date de départ *</Label>
              <Input
                type="date"
                value={formData.departure_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    departure_date: e.target.value,
                  }))
                }
                className="input-base"
              />
              <FieldError
                show={editSubmitAttempted && !formData.departure_date}
              >
                Date requise
              </FieldError>
            </div>

            <div>
              <div className="flex items-center gap-1">
                <Label className="label-base">Trajet (corridor) *</Label>
                <Aide titre="Trajet (corridor)">
                  {useCorridorPicker
                    ? 'Le corridor porte le tracé, la distance restante et les postes frontières qui font avancer le trajet.'
                    : "Aucun corridor n'est encore installé: le 1er et le dernier pays servent pour les trajets."}
                </Aide>
              </div>
              {useCorridorPicker ? (
                <>
                  <SearchableSelect
                    className="input-base"
                    data-testid="edit-operation-route"
                    placeholder="Choisir un corridor"
                    searchPlaceholder="Rechercher un corridor…"
                    emptyMessage="Aucun corridor trouvé"
                    value={formData.corridor_id || ''}
                    onValueChange={selectCorridor}
                    options={corridorOptions}
                  />
                  <FieldError show={editSubmitAttempted && !formData.corridor_id}>
                    Corridor requis
                  </FieldError>
                  {!formData.corridor_id && formData.route ? (
                    <p className="text-xs text-muted-foreground mt-1">
                      Itinéraire actuel: {formData.route}
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <Input
                    placeholder="Ex: Mali - Sénégal - Mali"
                    value={formData.route}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, route: e.target.value }))
                    }
                    className="input-base"
                    data-testid="edit-operation-route"
                  />
                  <FieldError show={editSubmitAttempted && !formData.route}>
                    Champ requis
                  </FieldError>
                </>
              )}

              {Array.isArray(formData.truck_ids) &&
              formData.truck_ids.length > 0 ? (
                <p className="text-xs text-muted-foreground mt-2">
                  Camions (non modifiable ici): {formData.truck_ids.length}
                </p>
              ) : null}
            </div>

            <div>
              <Label className="label-base">Description</Label>
              <Textarea
                placeholder="Description du convoi..."
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="input-base min-h-[80px]"
              />
            </div>

            <ConvoyOptionalFields
              form={formData}
              setForm={setFormData}
              driverOptions={driverOptions}
              idPrefix="edit-operation"
            />

            <div className="flex items-center justify-between">
              <Label className="label-base mb-0">Convoi en cours</Label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, active: checked }))
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setOperationToEdit(null);
              }}
            >
              Annuler
            </Button>
            <Button
              className="btn-primary"
              onClick={handleEditSubmit}
              disabled={
                isSubmitting ||
                !formData.code ||
                !formData.departure_date ||
                !formData.route ||
                Boolean(returnDateError(formData))
              }
              data-testid="submit-edit-operation"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Enregistrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce convoi ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les trajets et l’historique liés à ce convoi seront aussi
              supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setOperationToDelete(null);
              }}
            >
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Supprimer'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};


export default OperationsPage;
